/**
 * /equipment/pressure-control — the register of pressure-control components
 * (BOPs, rams, annulars, spools, hoses …), api#1268 BOP-1. Full width; a row
 * opens the component on its own page (PressureControlDetailScreen).
 *
 * SHARED, NOT A VERTICAL. No `hasVertical` guard — a component is a row of the
 * shared `equipment` table, and the API mounts this module with no vertical
 * guard either. The way in is the band button on /equipment, plus the panel on
 * an Equipment detail page wherever the API says the unit is a component.
 *
 * Every status, last valid day and day count on this page is the API's
 * certState; nothing is computed here. FILTER CHIPS live in the URL
 * (`?status=`) so the back arrow from a record returns to the list as it was
 * left. THEIR COUNTS ARE A TALLY OF THE LOADED ROWS: GET /components is
 * unpaginated and no endpoint returns the figures.
 */
import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Loader2, Plus, Settings2 } from 'lucide-react';
import { api } from '../../api/client';
import { unwrapArray } from '../../lib/api-envelope';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useCanWriteCompanySettings } from '../../hooks/useCanWriteCompanySettings';
import { useCreatePcComponent, usePcComponents, usePcMeta } from '../../hooks/usePressureControl';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import {
  canAdminPc,
  PC_LIST,
  PC_STATUS_LABEL,
  PC_STATUS_RAIL,
  pcClassLabel,
  pcDay,
  pcDaysPhrase,
} from '../../lib/pressure-control';
import { PC_CERT_STATUSES, type PcComponent } from '../../types/pressure-control';
import { PcStatusBadge } from '../../components/pressure-control/PcCertState';
import { PcSectionNav } from '../../components/pressure-control/PcSectionNav';
import { PcModuleStatusPanel } from '../../components/pressure-control/PcModuleStatusPanel';
import { PcSpecDialog, type PcEquipmentOption } from '../../components/pressure-control/PcSpecDialog';
import { DH_BAND_SECONDARY, DhEmpty, DhPanel } from '../downhole/DownholeBits';
import { useOpenRecord } from '../downhole/recordPage';

const FILTERS: Array<{ key: string; label: string }> = [
  { key: 'all', label: 'All' },
  ...PC_CERT_STATUSES.map((s) => ({ key: s, label: PC_STATUS_LABEL[s] })),
];

/** "Unit 12 · S/N 4471", or `—`. */
export const pcIdentity = (c: Pick<PcComponent, 'unitNumber' | 'serialNumber'>) =>
  [c.unitNumber ? `Unit ${c.unitNumber}` : null, c.serialNumber ? `S/N ${c.serialNumber}` : null].filter(Boolean).join(' · ') || '—';

export default function PressureControlScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const elevated = useCanWriteCompanySettings();
  const canAdmin = canAdminPc(user?.role, elevated);
  const [adding, setAdding] = useState(false);

  const statusParam = params.get('status');
  const active = statusParam && FILTERS.some((f) => f.key === statusParam) ? statusParam : 'all';

  const query = usePcComponents();
  const metaQuery = usePcMeta();
  const create = useCreatePcComponent();
  const failure = queryLoadFailure(query);
  const all = useMemo(() => query.data?.rows ?? [], [query.data]);
  const rows = active === 'all' ? all : all.filter((c) => c.certState?.status === active);
  const tally = (key: string) => (key === 'all' ? all.length : all.filter((c) => c.certState?.status === key).length);

  // Equipment not yet registered, for the Register dialog. Only fetched when it opens.
  const equipmentQuery = useQuery({
    queryKey: ['equipment', 'list', 'pc-register'],
    queryFn: async () => unwrapArray<any>((await api.equipment.list()).data),
    enabled: adding,
  });
  const registered = useMemo(() => new Set(all.map((c) => c.equipmentId)), [all]);
  const equipmentOptions: PcEquipmentOption[] = (equipmentQuery.data ?? [])
    .filter((e) => e?.id && !registered.has(e.id))
    .map((e) => ({
      id: e.id,
      name: e.name ?? '—',
      detail: pcIdentity({ unitNumber: e.unitNumber ?? e.unit_number ?? null, serialNumber: e.serialNumber ?? e.serial_number ?? null }),
    }));
  const stacks = all.filter((c) => c.spec.componentClass === 'stack_assembly').map((c) => ({ equipmentId: c.equipmentId, name: c.name }));

  const open = (id: string) => openRecord(`${PC_LIST}/${id}`);
  const rowKeys = (id: string) => (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      open(id);
    }
  };

  return (
    <div data-testid="pressure-control-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Equipment"
        title="Pressure control"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canAdmin ? (
            <>
              <Link to="/settings/pressure-control" className={DH_BAND_SECONDARY}>
                <Settings2 className="size-4" aria-hidden="true" />
                Settings
              </Link>
              <button type="button" onClick={() => setAdding(true)} className="fi-action fi-action--standard fi-action--primary">
                <Plus className="size-4" aria-hidden="true" />
                Register component
              </button>
            </>
          ) : undefined
        }
        /* No stat strip: the chips below carry every count a strip would. */
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <PcSectionNav />
          <PcModuleStatusPanel />
          <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by certificate status">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                aria-pressed={active === f.key}
                onClick={() => {
                  const next = new URLSearchParams(params);
                  if (f.key === 'all') next.delete('status');
                  else next.set('status', f.key);
                  setParams(next, { replace: true });
                }}
                className={`inline-flex items-center gap-2 rounded-fi-pill border px-3 py-1.5 text-sm font-bold transition ${
                  active === f.key ? 'border-fi-amber bg-fi-signal-amber text-fi-signal-ink' : 'border-fieldiq-border bg-fieldiq-card text-fieldiq-text hover:bg-fieldiq-dark/60'
                }`}
              >
                {f.label}
                {!query.isLoading && !failure && <span className="font-mono text-xs tabular-nums">{tally(f.key)}</span>}
              </button>
            ))}
          </div>

          <DhPanel
            eyebrow="BOP certificates"
            title="Components"
            testId="pc-register"
            footer={
              !query.isLoading && !failure && all.length > 0
                ? `Showing ${rows.length} of ${all.length}${canAdmin ? '' : ' · only an admin or office staff can register a component or record a certificate'}`
                : undefined
            }
          >
            {query.isLoading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading components" />
              </div>
            ) : failure ? (
              <LoadFailureNotice
                error={queryFailureError(query)}
                kind={failure}
                subject="pressure-control components"
                allowed="admins, office staff, supervisors, dispatchers, field workers and logistics"
              />
            ) : all.length === 0 ? (
              <DhEmpty>
                No equipment is registered for pressure control yet.
                {canAdmin && ' Register a BOP, ram, spool or hose with Register component.'}
              </DhEmpty>
            ) : rows.length === 0 ? (
              <DhEmpty>No component is {PC_STATUS_LABEL[active]?.toLowerCase() ?? active}.</DhEmpty>
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[900px] text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                      <tr className="border-l-4 border-l-transparent">
                        <th scope="col" className="px-4 py-3">Component</th>
                        <th scope="col" className="px-4 py-3">Class</th>
                        <th scope="col" className="px-4 py-3">Status</th>
                        <th scope="col" className="px-4 py-3">Valid through</th>
                        <th scope="col" className="px-4 py-3">Days remaining</th>
                        <th scope="col" className="px-4 py-3">Certified</th>
                        <th scope="col" className="px-4 py-3">Stack</th>
                        <th scope="col" className="w-[44px] px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((c) => (
                        <tr
                          key={c.equipmentId}
                          data-testid="pc-row"
                          role="button"
                          tabIndex={0}
                          onClick={() => open(c.equipmentId)}
                          onKeyDown={rowKeys(c.equipmentId)}
                          className={`cursor-pointer border-l-4 border-t border-fieldiq-border outline-none transition hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${PC_STATUS_RAIL[c.certState?.status] ?? 'border-l-transparent'}`}
                        >
                          <td className="px-4 py-3">
                            <span className="block text-sm font-extrabold text-white">{c.name ?? '—'}</span>
                            <span className="block text-xs text-fieldiq-muted">{pcIdentity(c)}</span>
                          </td>
                          <td className="px-4 py-3 text-sm text-fieldiq-text">{pcClassLabel(c.spec.componentClass)}</td>
                          <td className="px-4 py-3"><PcStatusBadge status={c.certState?.status} /></td>
                          <td className="px-4 py-3 text-sm text-fieldiq-text">{pcDay(c.certState?.expiresOn)}</td>
                          <td className="px-4 py-3 font-mono text-sm tabular-nums text-fieldiq-text">{pcDaysPhrase(c.certState?.daysUntilExpiry)}</td>
                          <td className="px-4 py-3 text-sm text-fieldiq-text">{pcDay(c.currentCertification?.certifiedOn)}</td>
                          <td className="px-4 py-3 text-sm text-fieldiq-text">{c.spec.stackParentName ?? '—'}</td>
                          <td className="px-4 py-3"><ChevronRight className="size-4 text-fieldiq-muted" aria-hidden="true" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="space-y-3 p-4 md:hidden">
                  {rows.map((c) => (
                    <div
                      key={c.equipmentId}
                      role="button"
                      tabIndex={0}
                      onClick={() => open(c.equipmentId)}
                      onKeyDown={rowKeys(c.equipmentId)}
                      className="cursor-pointer rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3 outline-none transition hover:border-fieldiq-muted focus-visible:border-fi-amber focus-visible:bg-fi-amber/10"
                    >
                      <p className="flex flex-wrap items-center gap-2 text-sm font-extrabold text-white">
                        {c.name ?? '—'}
                        <PcStatusBadge status={c.certState?.status} />
                      </p>
                      <p className="mt-1 text-xs text-fieldiq-muted">
                        {pcClassLabel(c.spec.componentClass)} · {pcIdentity(c)}
                      </p>
                      <p className="mt-1 text-xs text-fieldiq-text">
                        Valid through {pcDay(c.certState?.expiresOn)} · {pcDaysPhrase(c.certState?.daysUntilExpiry)}
                      </p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </DhPanel>
        </div>
      </section>

      {canAdmin && (
        <PcSpecDialog
          open={adding}
          onClose={() => setAdding(false)}
          meta={metaQuery.data}
          pressureUnit={query.data?.meta?.pressureUnit}
          equipmentOptions={equipmentOptions}
          equipmentState={equipmentQuery.isError ? 'error' : equipmentQuery.data ? 'ready' : 'loading'}
          stacks={stacks}
          pending={create.isPending}
          onSubmit={async (body) => {
            const created = await create.mutateAsync(body);
            if (created?.equipmentId) open(created.equipmentId);
          }}
        />
      )}
    </div>
  );
}
